import { gql } from '@apollo/client';

/**
 * Workflows (plan 4.5). API card 17.5; every request with a real response is in
 * ilbp-backend docs/CRM_CONTACT_QUERY_FRONTEND.md §31.4. Admin-only.
 *
 * Starting, enrolling and copying are metered with bulk tagging (`crm-bulk`,
 * 10 a minute); everything else with the admin's 120 root fields a minute.
 */

const WORKFLOW_FIELDS = `
  id
  name
  description
  source
  status
  nbStatus
  triggerType
  triggerSegmentId
  triggerSegmentName
  triggerTagId
  triggerTagName
  includeExisting
  exitTagIds
  allowReentry
  reentryCooldownH
  maxPerContactPerDay
  maxSendsPerDay
  quietHoursStart
  quietHoursEnd
  stepCount
  enrollments {
    active
    completed
    excluded
    canceled
    failed
    total
  }
  startedAt
  pausedAt
  endedAt
  lastEnrolledAt
  createdAt
  updatedAt
`;

const STEP_FIELDS = `
  id
  position
  type
  delayMinutes
  delayUntilDow
  delayUntilHour
  emailCampaignId
  emailCampaignName
  emailSubject
  message
  title
  tagId
  tagName
  note
  dueInHours
  assignToAdminId
  nbAfterEnrolmentMinutes
  waiting
  executed
`;

export const CRM_WORKFLOWS = gql`
  query CrmWorkflows($status: [WorkflowStatus!], $search: String, $source: String, $skip: Int, $take: Int) {
    crmWorkflows(status: $status, search: $search, source: $source, skip: $skip, take: $take) {
      total
      items {
        ${WORKFLOW_FIELDS}
      }
    }
  }
`;

/** The tiles above the list: five root fields, asked once a visit and again after a change. */
export const CRM_WORKFLOW_TOTALS = gql`
  query CrmWorkflowTotals {
    running: crmWorkflows(status: [RUNNING], source: "MANUAL", take: 1) {
      total
    }
    drafts: crmWorkflows(status: [DRAFT], source: "MANUAL", take: 1) {
      total
    }
    paused: crmWorkflows(status: [PAUSED], source: "MANUAL", take: 1) {
      total
    }
    ended: crmWorkflows(status: [ENDED, ARCHIVED], source: "MANUAL", take: 1) {
      total
    }
    migrated: crmWorkflows(source: "NATIONBUILDER", take: 1) {
      total
    }
  }
`;

export const CRM_WORKFLOW = gql`
  query CrmWorkflow($id: Int!) {
    crmWorkflow(id: $id) {
      ${WORKFLOW_FIELDS}
      steps {
        ${STEP_FIELDS}
      }
      origins {
        originType
        originId
        segmentId
        segmentName
        enrollments
      }
    }
  }
`;

const SAVED = `
  ${WORKFLOW_FIELDS}
  steps {
    ${STEP_FIELDS}
  }
`;

export const CRM_CREATE_WORKFLOW = gql`
  mutation CrmCreateWorkflow($input: CrmWorkflowInput!, $steps: [CrmWorkflowStepInput!]) {
    crmCreateWorkflow(input: $input, steps: $steps) {
      ${SAVED}
    }
  }
`;

export const CRM_UPDATE_WORKFLOW = gql`
  mutation CrmUpdateWorkflow($id: Int!, $input: CrmWorkflowInput!) {
    crmUpdateWorkflow(id: $id, input: $input) {
      ${SAVED}
    }
  }
`;

export const CRM_SET_WORKFLOW_STEPS = gql`
  mutation CrmSetWorkflowSteps($id: Int!, $steps: [CrmWorkflowStepInput!]!) {
    crmSetWorkflowSteps(id: $id, steps: $steps) {
      ${SAVED}
    }
  }
`;

export const CRM_DUPLICATE_WORKFLOW = gql`
  mutation CrmDuplicateWorkflow($id: Int!) {
    crmDuplicateWorkflow(id: $id) {
      id
      name
      status
      stepCount
    }
  }
`;

export const CRM_DELETE_WORKFLOW = gql`
  mutation CrmDeleteWorkflow($id: Int!) {
    crmDeleteWorkflow(id: $id)
  }
`;

const STATE_FIELDS = `
  id
  status
  startedAt
  pausedAt
  endedAt
  enrollments {
    active
    completed
    excluded
    canceled
    failed
    total
  }
`;

export const CRM_START_WORKFLOW = gql`
  mutation CrmStartWorkflow($id: Int!) {
    crmStartWorkflow(id: $id) {
      ${STATE_FIELDS}
    }
  }
`;

export const CRM_PAUSE_WORKFLOW = gql`
  mutation CrmPauseWorkflow($id: Int!) {
    crmPauseWorkflow(id: $id) {
      ${STATE_FIELDS}
    }
  }
`;

export const CRM_END_WORKFLOW = gql`
  mutation CrmEndWorkflow($id: Int!) {
    crmEndWorkflow(id: $id) {
      ${STATE_FIELDS}
    }
  }
`;

export const CRM_ARCHIVE_WORKFLOW = gql`
  mutation CrmArchiveWorkflow($id: Int!) {
    crmArchiveWorkflow(id: $id) {
      ${STATE_FIELDS}
    }
  }
`;

/** Who it would enrol now and what each step could reach. Sends nothing. */
export const CRM_SIMULATE_WORKFLOW = gql`
  query CrmSimulateWorkflow($id: Int!, $filter: CrmContactFilter) {
    crmSimulateWorkflow(id: $id, filter: $filter) {
      matched
      wouldEnroll
      steps {
        position
        type
        afterMinutes
        reach
      }
      sample {
        id
        fullName
        canEmail
        canText
        hasAccount
      }
    }
  }
`;

export const CRM_ENROLL_COUNT = gql`
  query CrmEnrollCount($filter: CrmContactFilter) {
    crmContactCounts(filter: $filter) {
      total
    }
  }
`;

export const CRM_ENROLL_IN_WORKFLOW = gql`
  mutation CrmEnrollInWorkflow($id: Int!, $filter: CrmContactFilter!, $expectedCount: Int!) {
    crmEnrollInWorkflow(id: $id, filter: $filter, expectedCount: $expectedCount) {
      matched
      enrolled
      skipped
    }
  }
`;

export const CRM_WORKFLOW_ENROLLMENTS = gql`
  query CrmWorkflowEnrollments($workflowId: Int, $status: EnrollmentStatus, $skip: Int, $take: Int) {
    crmWorkflowEnrollments(workflowId: $workflowId, status: $status, skip: $skip, take: $take) {
      total
      items {
        id
        contactId
        contactName
        status
        currentStep
        nextStepAt
        nextStepStatus
        originType
        enrolledAt
        completedAt
        excludedAt
        excludedReason
      }
    }
  }
`;

export const CRM_ENROLLMENT_ACTIONS = gql`
  query CrmWorkflowEnrollmentActions($enrollmentId: Int!) {
    crmWorkflowEnrollmentActions(enrollmentId: $enrollmentId) {
      id
      stepId
      position
      type
      status
      executeAt
      executedAt
      attempts
      result
      lastError
    }
  }
`;

export const CRM_REMOVE_FROM_WORKFLOW = gql`
  mutation CrmRemoveFromWorkflow($enrollmentId: Int!) {
    crmRemoveFromWorkflow(enrollmentId: $enrollmentId) {
      id
      status
      excludedAt
      excludedReason
    }
  }
`;

/* ------------------------------------------------------------------ *
 * Choices for the editor
 * ------------------------------------------------------------------ */

/** Lists and saved filters by name, for a trigger, an email's audience or enrolling. */
export const CRM_SEGMENT_CHOICES = gql`
  query CrmSegmentChoices($search: String) {
    crmSegments(search: $search, take: 50) {
      items {
        id
        name
        type
        isActive
        contactCount
      }
    }
  }
`;

/** Draft campaigns of ours, which is what an email step may send. */
export const CRM_DRAFT_CAMPAIGN_CHOICES = gql`
  query CrmDraftCampaignChoices($search: String) {
    crmEmailCampaigns(status: [DRAFT], source: "MANUAL", search: $search, take: 50) {
      items {
        id
        name
        subject
        workflowStepCount
      }
    }
  }
`;

export const CRM_QUICK_CAMPAIGN = gql`
  mutation CrmQuickCampaign($input: CrmEmailCampaignInput!) {
    crmCreateEmailCampaign(input: $input) {
      id
      name
      subject
      workflowStepCount
    }
  }
`;
